import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {getGroundingReasonDisplayValue} from '../../../utils/getGroundingReasonDisplayValue';
import {getCheckinData} from './getCheckinData';
import {getHandoverData} from './getHandoverData';
import {getServiceCampaignData} from './getServiceCampaignData';
import {getServiceOrderData} from './getServiceOrderData';

export const useAttributesData = (type: string, dataJson: string | Nullish) => {
  const formatDateTime = useDateTimeFormatter();

  if (!dataJson) {
    return undefined;
  }

  return match(type)
    .with('checkin', () => {
      const data = getCheckinData(dataJson);

      return [
        {
          label: i18n.t('entity.vehicle.labels.mileage'),
          value: data.mileage || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.workshop.labels.fuelTank'),
          value: data.fuelAmount || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.xentry.labels.completedDate'),
          value: data.completedDate
            ? formatDateTime('dateTimeShort', parseDate(data.completedDate))
            : EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.xentry.labels.scheduledDate'),
          value: data.scheduledDate
            ? formatDateTime('dateTimeShort', parseDate(data.scheduledDate))
            : EMPTY_PLACEHOLDER,
        },
      ];
    })
    .with('handover', () => {
      const data = getHandoverData(dataJson);

      return [
        {
          label: i18n.t('entity.vehicle.labels.mileage'),
          value: data.mileage || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.workshop.labels.fuelTank'),
          value: data.fuelAmount || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.xentry.labels.completedDate'),
          value: data.completedDate
            ? formatDateTime('dateTimeShort', parseDate(data.completedDate))
            : EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.xentry.labels.scheduledDate'),
          value: data.scheduledDate
            ? formatDateTime('dateTimeShort', parseDate(data.scheduledDate))
            : EMPTY_PLACEHOLDER,
        },
      ];
    })
    .with('service-order', () => {
      const data = getServiceOrderData(dataJson);

      return [
        {
          label: i18n.t('general.labels.title'),
          value: data.title || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('general.labels.number'),
          value: data.number || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('general.labels.variant'),
          value: data.variant || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('general.labels.note'),
          value: data.note || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.damageCode'),
          value: data.damageCode || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.eskulabAuthorizationNumber'),
          value: data.eskulabAuthorizationNumber || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.eskulabStatus'),
          value: data.eskulabStatus || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.eskulabValidityDate'),
          value: data.eskulabValidityDate
            ? formatDateTime('dateTimeShort', parseDate(data.eskulabValidityDate))
            : EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.eskulabValidityMileageInKm'),
          value: data.eskulabValidityMileageInKm || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.groundingReasonAttribute'),
          value: getGroundingReasonDisplayValue(data.groundingReason),
        },
      ];
    })
    .with('service-campaign', () => {
      const data = getServiceCampaignData(dataJson);

      return [
        {
          label: i18n.t('general.labels.title'),
          value: data.title || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('general.labels.note'),
          value: data.note || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.damageCode'),
          value: data.damageCode || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('entity.serviceOrder.labels.reasonForRejection'),
          value: data.reasonForRejection || EMPTY_PLACEHOLDER,
        },
        {
          label: i18n.t('general.labels.status'),
          value: data.status || EMPTY_PLACEHOLDER,
        },
      ];
    })
    .otherwise(() => undefined);
};
